import { useContext } from "react";
import { RivetsApiError, type OrgMember, type WorkerAccessGrant, type WorkerAccessTeam, type WorkerInfo } from "@rivets/core";
import { memberName, OrgContext } from "@/lib/OrgContext";

/**
 * Who may run work on a worker, as the web client reads it.
 *
 * The server is the authority: `canUse` is computed per viewer and every
 * refused action comes back as a 403. This only decides what to offer.
 */
export function workerUsable(worker: Pick<WorkerInfo, "canUse">): boolean {
  return worker.canUse;
}

/** The workers a picker may offer: only ones the viewer can run work on. */
export function usableWorkers<W extends Pick<WorkerInfo, "canUse">>(workers: readonly W[]): W[] {
  return workers.filter(workerUsable);
}

/** Shown wherever routing has nothing the viewer may use. */
export const NO_SHARED_WORKER_MESSAGE = "No worker is shared with you — ask a teammate to share one or register your own.";

export interface WorkerAccessBlock {
  workerName: string;
  ownerName: string;
  /** A tooltip-length reason, for disabled buttons and status rows. */
  reason: string;
  /** The composer's sentence: what you can still do, and why not the rest. */
  message: string;
}

/**
 * Why the viewer cannot act on a task whose worktree lives on `worker`, or
 * null when they can (or the worker has not loaded yet: read-only views and
 * the server's own refusal cover that window).
 */
export function workerAccessBlock(
  worker: Pick<WorkerInfo, "name" | "ownerUserId" | "canUse"> | undefined,
  membersById: Map<string, OrgMember>,
): WorkerAccessBlock | null {
  if (!worker || workerUsable(worker)) return null;
  const ownerName = worker.ownerUserId ? memberName(membersById, worker.ownerUserId) : "Its owner";
  return {
    workerName: worker.name,
    ownerName,
    reason: `${worker.name} isn't shared with you`,
    message: `This task runs on ${worker.name}, which ${ownerName} hasn't shared with you. You can follow along but not send messages.`,
  };
}

/** `workerAccessBlock` with the org's roster. Tolerates rendering outside the
 * org shell (isolated component tests), where names fall back to short ids. */
export function useWorkerAccessBlock(worker: Pick<WorkerInfo, "name" | "ownerUserId" | "canUse"> | undefined): WorkerAccessBlock | null {
  const membersById = useContext(OrgContext)?.membersById ?? EMPTY_MEMBERS;
  return workerAccessBlock(worker, membersById);
}

const EMPTY_MEMBERS = new Map<string, OrgMember>();

/** The friendly sentence for a refusal about worker access, or undefined for
 * any other error. Covers REST 403s and org-socket error frames alike.
 * `worker_cluster_access_denied` is left to the server's own message: it
 * says whose access is missing — the caller's, or an automation creator's. */
export function workerAccessErrorText(error: unknown): string | undefined {
  const code = error instanceof RivetsApiError ? error.code : typeof error === "string" ? error : undefined;
  switch (code) {
    case "worker_access_denied":
      return "This worker isn't shared with you. Ask its owner to share it, or use a worker of your own.";
    case "worker_owner_required":
      return "Only this worker's owner can change that.";
    default:
      return undefined;
  }
}

/** A mutation's error line: the access sentence when it is one, the server's
 * message for any other API error, the fallback otherwise. */
export function apiErrorText(error: unknown, fallback: string): string {
  return workerAccessErrorText(error) ?? (error instanceof RivetsApiError ? error.message : error instanceof Error && error.message ? error.message : fallback);
}

export interface AccessSummaryContext {
  viewerId?: string;
  orgName?: string;
  teams: readonly WorkerAccessTeam[];
  membersById: Map<string, OrgMember>;
}

/**
 * One line naming who may use the worker: "Only you", "Everyone in Acme",
 * "Design team, Ada". The owner is implied by every grant list, so it is only
 * named when there is nobody else.
 */
export function workerAccessSummary(worker: Pick<WorkerInfo, "ownerUserId" | "access">, context: AccessSummaryContext): string {
  const access = worker.access ?? [];
  if (access.length === 0) {
    return worker.ownerUserId === context.viewerId ? "Only you" : `Only ${memberName(context.membersById, worker.ownerUserId)}`;
  }
  if (access.some((grant) => grant.kind === "everyone")) return `Everyone in ${context.orgName ?? "the organization"}`;
  const teams = access
    .flatMap((grant) => grant.kind === "team" ? [teamLabel(context.teams.find((team) => team.id === grant.teamId)?.name)] : [])
    .sort((left, right) => left.localeCompare(right));
  const users = access
    .flatMap((grant) => grant.kind === "user" ? [grant.userId === context.viewerId ? "you" : memberName(context.membersById, grant.userId)] : [])
    .sort((left, right) => left === "you" ? -1 : right === "you" ? 1 : left.localeCompare(right));
  const names = [...teams, ...users];
  const shown = names.slice(0, SUMMARY_NAMES);
  const rest = names.length - shown.length;
  const line = `${shown.join(", ")}${rest > 0 ? ` and ${rest} more` : ""}`;
  return line.charAt(0).toUpperCase() + line.slice(1);
}

const SUMMARY_NAMES = 3;

/** "Design team"; a team already called "… team" is not doubled. */
function teamLabel(name: string | undefined): string {
  // Not in `WorkersResponse.teams`: deleted since, or not listed to this viewer.
  if (!name) return "A team";
  return /\bteam$/i.test(name.trim()) ? name.trim() : `${name.trim()} team`;
}

/** The editor's state: one switch for the whole org, else picked teams and
 * members. Choosing everyone subsumes the rest, so it is saved alone. */
export interface WorkerAccessDraft {
  everyone: boolean;
  teamIds: string[];
  userIds: string[];
}

export function accessDraftFrom(access: readonly WorkerAccessGrant[] | undefined): WorkerAccessDraft {
  const grants = access ?? [];
  return {
    everyone: grants.some((grant) => grant.kind === "everyone"),
    teamIds: grants.flatMap((grant) => grant.kind === "team" ? [grant.teamId] : []),
    userIds: grants.flatMap((grant) => grant.kind === "user" ? [grant.userId] : []),
  };
}

export function accessFromDraft(draft: WorkerAccessDraft, ownerUserId: string): WorkerAccessGrant[] {
  if (draft.everyone) return [{ kind: "everyone" }];
  return [
    ...[...new Set(draft.teamIds)].map((teamId) => ({ kind: "team" as const, teamId })),
    // The owner always has access; granting it again is noise in the list.
    ...[...new Set(draft.userIds)].filter((userId) => userId !== ownerUserId).map((userId) => ({ kind: "user" as const, userId })),
  ];
}

/** Who may change a worker's grants or hand it on: its owner, and nobody
 * else — an organization owner included. */
export function canManageWorkerAccess(worker: Pick<WorkerInfo, "ownerUserId">, viewerId: string | undefined): boolean {
  return Boolean(viewerId) && worker.ownerUserId === viewerId;
}

/** Who may revoke a worker: its owner, or an organization owner. */
export function canRemoveWorker(worker: Pick<WorkerInfo, "ownerUserId">, viewerId: string | undefined, role: "owner" | "member" | undefined): boolean {
  return role === "owner" || canManageWorkerAccess(worker, viewerId);
}

/** Clusters a registration may join: any for an organization owner, otherwise
 * only clusters where the registrant already owns a worker. */
export function joinableClusters(
  workers: readonly Pick<WorkerInfo, "clusterId" | "clusterName" | "ownerUserId">[],
  viewerId: string | undefined,
  role: "owner" | "member" | undefined,
): { id: string; name: string }[] {
  const clusters = new Map<string, string>();
  for (const worker of workers) {
    if (role === "owner" || (viewerId && worker.ownerUserId === viewerId)) clusters.set(worker.clusterId, worker.clusterName);
  }
  return [...clusters.entries()].map(([id, name]) => ({ id, name }));
}
